import { BrandList, LegacyBrandList } from './enums.js';
import { XymaticVideoStatusEnum } from './types.js';
export * from './mergeSchemas.js';
export const date = {
    type: 'object',
    instanceof: 'Date',
    tsType: 'Date',
};
export const dateOrString = {
    oneOf: [{ type: 'string' }, date],
};
export const dateOrNumber = {
    oneOf: [{ type: 'number' }, date],
};
export const contentManagementSystems = {
    enum: ['vibe', 'bunte-stack', 'escenic', 'interred', 'praxistipps'],
    description: 'BurdaForward CMS names',
};
export const uuid = {
    type: 'string',
    format: 'uuid',
    description: 'UUID v4\n@link https://en.wikipedia.org/wiki/Universally_unique_identifier#Version_4_(random)',
};
export const brand = {
    enum: [...BrandList],
    description: 'Brand content',
};
export const crossBrand = {
    enum: ['bunte', 'focus', 'chip', 'tv_spielfilm', 'efahrer', 'finanzen100', 'netmoms', 'fitforfun', 'weather_channel'],
    description: 'Cross brand',
};
export const contentStateValues = ['draft', 'schedule', 'publish', 'unpublish'];
export const videoStates = contentStateValues.filter((s) => s === 'schedule' || s === 'publish' || s === 'unpublish');
export const state = {
    enum: contentStateValues,
    description: `State in which entity can be found itself:
  draft: Shown only in editor, not shown in OPM
  schedule: Will be shown at future date, not shown in OPM presently
  publish: Is visible in OPM
  unpublish: Entity is expired and no longer accessible in OPM`,
};
export const commonPathProperties = {
    path: {
        type: 'string',
        description: 'the url without domain but with extension, aka. url path segment',
    },
    type: {
        enum: ['generated', 'imported', 'custom'],
        description: `
generated: created by an internal rule (typically slug is created from categories, SEO title and UUID)
imported: set via api - see legacy-push case or later news migration
custom: editor manually changes url-field in vibe`,
    },
    createDate: date,
    articlePathBase: {
        type: 'string',
        description: 'the base path used for article URLs, different from the channel page path (e.g., /news/ for articles vs /nachrichten/ for channel pages)',
    },
};
export const path = {
    type: 'object',
    title: 'IPath',
    additionalProperties: false,
    properties: { ...commonPathProperties },
    required: ['path', 'type', 'createDate'],
};
export const brandAwarePath = {
    type: 'object',
    title: 'IBrandAwarePath',
    additionalProperties: false,
    properties: {
        ...commonPathProperties,
        brand: {
            oneOf: [brand],
            description: 'the brand the path has been assigned to',
        },
        createDate: date,
    },
    required: ['path', 'brand', 'type', 'createDate'],
};
/**
 * Allows scheduling/publishing of entities at given time
 */
export const publishableMixin = {
    properties: {
        unpublishDate: { oneOf: [date], description: 'Date when entity should be taken down automatically.' },
        displayDate: {
            oneOf: [date],
            description: 'Date set by the editor, and is the date shown in the entity instead of actual publish date.',
        },
        publishDate: {
            oneOf: [date],
            description: 'Date when entity was published (or is scheduled to be published at). If undefined then entity should not be published.',
        },
        state,
    },
    required: ['state'],
    schedulable: ['displayDate', 'publishDate', 'unpublishDate'],
};
/**
 * Allows tracking when entity was last changed
 */
export const changesTrackableMixin = {
    properties: {
        createDate: { oneOf: [date], description: 'Date when entity was created.' },
        changeDate: { oneOf: [date], description: 'Date when entity was last changed.' },
    },
    required: ['createDate', 'changeDate'],
};
/**
 * Allows entity to be deleted logically, but still persisted in database. Entity is not present in lists/details
 */
export const softDeletableMixin = {
    properties: {
        isDeleted: {
            type: 'boolean',
            description: 'Soft deletable: Entity is set to deleted, but is actually kept in the database.',
        },
    },
    required: ['isDeleted'],
};
/**
 * Allows entity to be enabled or disabled. If disabled, entity is not able to be used, but is still present in listing/details
 */
export const enableableMixin = {
    properties: {
        isEnabled: {
            type: 'boolean',
            description: 'Entity can be disabled, but still present in listings',
        },
    },
    required: ['isEnabled'],
};
/**
 * Allows entity to be restricted to a specific brand or multiple brands
 */
export const licensableMixin = {
    properties: {
        license: { type: 'array', items: brand, description: 'Brand to which entity can be published... e.g. Bunte Focus Chip' },
    },
    required: ['license'],
};
/**
 * Allows entity to be assigned to single brand.
 */
export const brandableMixin = {
    properties: {
        brand: brand,
    },
    required: ['brand'],
};
export const legacyBrandableMixin = {
    properties: {
        brand: {
            enum: [...LegacyBrandList],
            description: 'Brand content',
        },
    },
    required: ['brand'],
};
export const legacyMigrationFieldMixins = {
    type: 'object',
    additionalProperties: false,
    properties: {
        legacy: {
            type: 'object',
            additionalProperties: false,
            properties: {
                id: {
                    type: 'string',
                    description: 'ID for legacy content',
                },
                source: {
                    oneOf: [contentManagementSystems],
                    description: 'Source of the content',
                },
                isEditableInCTF: {
                    type: 'boolean',
                    description: 'Determine if the content can be edited in vibe',
                },
                hasBeenModifiedInCTF: {
                    type: 'boolean',
                    description: 'Determine if the content was changed in vibe',
                },
                isRenderedByCTF: {
                    type: 'boolean',
                    description: 'Determine if the production traffic is rendered by CTF',
                },
            },
            required: ['source', 'isEditableInCTF', 'hasBeenModifiedInCTF', 'isRenderedByCTF'],
        },
    },
    required: ['legacy'],
};
export const customTrackingMixin = {
    properties: {
        customTrackingId: {
            type: 'string',
            description: 'Tracking ID for legacy content, used for tracking purposes',
        },
    },
    required: [],
};
export const trackingMixin = {
    type: 'object',
    additionalProperties: false,
    properties: {
        tracking: {
            type: 'object',
            additionalProperties: false,
            description: 'Subdocument that contains all the tracking custom fields',
            properties: {
                pageContentSourceDetail: {
                    type: 'string',
                    enum: [
                        'kim',
                        'cat',
                        'PA_FOC, PA_FOCM',
                        'AIPA',
                        'expert-cockpit',
                        'contentlounge',
                        'discoversnoop',
                        'republisher',
                        'hubartikel',
                        'subartikel',
                        'faktencheck',
                        '',
                    ],
                    description: 'Field to track articles created with help of BF AI tool (kim) or via curated articles tool (cat)',
                },
                pageTags: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'Custom tags used in tracking of pageTag',
                },
            },
            required: ['pageContentSourceDetail'],
        },
    },
    required: ['tracking'],
};
export const metaTagsMixin = {
    type: 'object',
    additionalProperties: false,
    properties: {
        seoTitle: { type: 'string', description: 'meta title used for SEO' },
        seoDescription: { type: 'string', description: 'Short description used in html meta tags' },
        isNoIndex: { type: 'boolean', description: 'sets meta to noindex' },
    },
    required: [],
};
/**
 * creates the single reference for the schema relation
 */
export const schemaRelation = (propertyName, entityName, packageName) => {
    return {
        oneOf: [uuid],
        description: `UUID of the referenced ${entityName} will be bound to ${propertyName}`,
        entityReference: {
            entityName: entityName,
            bindingProperty: propertyName,
            entityPackageName: packageName,
        },
    };
};
/**
 * creates the many reference for the schema relation
 */
export const schemaRelationMany = (propertyName, entityName, packageName) => {
    return {
        type: 'array',
        description: `UUIDs of the referenced ${entityName} will be bound to ${propertyName}`,
        items: uuid,
        entityReference: {
            entityName: entityName,
            bindingProperty: propertyName,
            entityPackageName: packageName,
        },
    };
};
export const videoStatus = Object.values(XymaticVideoStatusEnum).filter((e) => isNaN(Number(e)));
/**
 * Allows entity to be marked as a test entity for precise cleanup during test runs
 */
export const testEntityMixin = {
    properties: {
        isTestEntity: {
            type: 'boolean',
            description: 'Marks entity as created during tests for precise cleanup.',
        },
    },
    required: [],
};
/**
 * Allows entity to have a table of contents text for navigation purposes
 */
export const tableOfContentsTextMixin = {
    properties: {
        tableOfContentsText: {
            type: 'string',
            description: 'Optional table of contents text for this block',
            maxLength: 100,
        },
    },
    required: [],
};
//# sourceMappingURL=definitions.js.map